/**
 * Monovits motion — L1 reveal engine.
 * Contract: savor-design-spec.md "Motion spec (2.2)", sections M1, M3, M4.
 *
 * One IntersectionObserver sets `data-mv-in` on `[data-mv-reveal]`; CSS owns
 * every transition.
 *
 * Two non-negotiables, both compliance-load-bearing:
 *
 *   1. The hidden state lives behind `[data-mv-armed]` on <html>, written by
 *      `init()` once the template hooks are applied and the observer exists,
 *      and before anything is scanned — and removed again in the catch path.
 *      If this module fails to parse, throws, is blocked, or never runs,
 *      nothing on the page is ever hidden. A claim,
 *      caution or disclaimer that needs a scroll event to become visible is a
 *      compliance failure (M3, no-JS rule).
 *   2. Nothing here ever hides an element that was not already carrying a
 *      `data-mv-reveal` hook chosen at authoring time, and no disclaimer,
 *      caution, Supplement Facts panel or price is a reveal target.
 *
 * Budget (M4): transform and opacity only, no scroll handlers, lazy init,
 * `will-change` set on approach and cleared on completion.
 */

const root = document.documentElement;
const REVEAL = '[data-mv-reveal]';

/**
 * Hooks for stock Savor sections, which we cannot annotate without editing
 * stock files. Keyed on `main[data-template]`; applied once at arm time only.
 *
 * Collection C1: the first row reveals, capped at 4. Everything below the fold
 * renders at final state, and filtered or infinite-scroll results are never
 * re-armed — restaggering filtered results is the generic pattern and delays
 * reading.
 */
const TEMPLATE_HOOKS = {
  collection: [['.product-grid__item', 4]],
};

/** Elements already handed to the observer, so re-scans stay idempotent. */
const seen = new WeakSet();

let observer = null;

/** Reveals every hook unconditionally. The direction of every failure path. */
function revealAll() {
  for (const el of document.querySelectorAll(REVEAL)) {
    el.dataset.mvIn = '';
    el.style.willChange = '';
  }
}

/** M4: clear `will-change` once the transition lands, never leave it standing. */
function settle(el) {
  const clear = () => {
    el.style.willChange = '';
    el.removeEventListener('transitionend', clear);
  };
  el.addEventListener('transitionend', clear);
  // The transition may never fire (element detached, display:none, morphed
  // away). Do not strand a compositor layer waiting for it.
  setTimeout(clear, 2000);
}

function onIntersect(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    const el = entry.target;
    observer.unobserve(el);
    // will-change on approach: rootMargin fires this ahead of the viewport,
    // and the reveal itself is deferred one frame so the hint lands first.
    el.style.willChange = 'opacity, transform';
    requestAnimationFrame(() => {
      el.dataset.mvIn = '';
      settle(el);
    });
  }
}

/**
 * Observes any hook not yet handled. Safe to call repeatedly: already-revealed
 * content is never re-armed, and elements appearing after the initial arm are
 * only observed if they were authored with the attribute — appended cards
 * never gain one, so they render at final state (C3).
 */
function scan(scope) {
  const container = scope && scope.querySelectorAll ? scope : document;
  for (const el of container.querySelectorAll(REVEAL)) {
    if (seen.has(el) || 'mvIn' in el.dataset) continue;
    seen.add(el);
    // Stagger index: position among sibling hooks. Lone hooks get 0.
    const siblings = el.parentElement ? el.parentElement.querySelectorAll(':scope > ' + REVEAL) : [el];
    if (siblings.length > 1) {
      el.style.setProperty('--mv-i', String([].indexOf.call(siblings, el)));
    }
    observer.observe(el);
  }
}

/** Applies the stock-section selector map. Once per page load, never on re-render. */
function applyTemplateHooks() {
  const main = document.querySelector('main[data-template]');
  const hooks = main && TEMPLATE_HOOKS[main.dataset.template];
  if (!hooks) return;
  for (const [selector, cap] of hooks) {
    const matches = main.querySelectorAll(selector);
    for (let i = 0; i < matches.length && i < cap; i++) {
      matches[i].dataset.mvReveal = 'row';
    }
  }
}

/**
 * Re-hook after a section re-render. R7: the theme dispatches no section
 * lifecycle event, so we extend the callback the renderer already runs after a
 * DOM morph, and fall back to a debounced MutationObserver if that module is
 * unavailable.
 */
function installRerenderHook() {
  let queued = false;
  const rescan = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan(document);
    });
  };

  import('@theme/morph')
    .then((mod) => {
      const options = mod && mod.MORPH_OPTIONS;
      if (!options || typeof options !== 'object') throw new Error('no MORPH_OPTIONS');
      const previous = options.onAfterUpdate;
      // Runs per morphed node, so the body must stay trivially cheap; the
      // actual re-scan is collapsed into one frame by `rescan`.
      options.onAfterUpdate = (node) => {
        if (typeof previous === 'function') previous(node);
        rescan();
      };
    })
    .catch(() => {
      const main = document.getElementById('MainContent');
      if (!main) return;
      let timer = 0;
      new MutationObserver(() => {
        clearTimeout(timer);
        timer = setTimeout(rescan, 120);
      }).observe(main, { childList: true, subtree: true });
    });
}

function init() {
  applyTemplateHooks();
  observer = new IntersectionObserver(onIntersect, { rootMargin: '160px 0px' });
  // Arming is the last thing that happens before the hidden state can apply.
  root.dataset.mvArmed = '';
  scan(document);
  installRerenderHook();

  // Failsafes. Every one of them resolves toward visible.
  addEventListener('pagehide', revealAll);
  addEventListener('beforeprint', revealAll);
}

// M3: reduced motion never arms, and a document that was not visible at load
// (prerender, background tab) is never left holding hidden content.
const reduced = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

if (
  !reduced &&
  document.visibilityState === 'visible' &&
  typeof IntersectionObserver === 'function' &&
  typeof requestAnimationFrame === 'function'
) {
  try {
    init();
  } catch (error) {
    // Arming may already have happened; undo it so nothing stays hidden.
    delete root.dataset.mvArmed;
    revealAll();
    if (window.console) console.warn('[monovits-motion] disabled:', error);
  }
}
